import React from "react";
import { safeJSONParse } from "../../utils/helper";
import { MessageInputHandle } from "../ChatWindow/MessageInput";
import UtilityAgent from "./UtilityAgent";
import UtilityAgentForm from "./UtilityAgentForm";
import PressReleaseAgent from "./PressReleaseAgent";
import PressReleaseQAAgent from "./PressReleaseQAAgent";
import LocalizerAgent from "./LocalizerAgent";
import TranslationAgent from "./TranslationAgent";
import MediaBriefAgent from "./MediaBriefAgent";
import SocialMediaInsightsAgent from "./SocialMediaInsightsAgent";
import InternalCommunicationAgent from "./InternalCommunicationAgent";
import WorkflowEndAgent from "./WorkflowEndAgent";
import PrMbWorkflowEndAgent from "./PrMbWorkflowEndAgent";
import SyntheticResumeAgent from "./SyntheticResumeAgent";
import ResumeFetcherAgent from "./ResumeFetcherAgent";
import ScoringAgent from "./ScoringAgent";
import EmailDraftingAgent from "./EmailDraftingAgent";
import HrAgentV2HitlAgent from "./HrAgentV2HitlAgent";
import ValidationCheckAgent from "./ValidationCheckAgent";
import NoCandidatesFoundAgent from "./NoCandidatesFoundAgent";
import TemplateReviewerAgent from "./TemplateReviewerAgent";
import ContractReviewerAgent from "./ContractReviewerAgent";
import TemplateReviewerIntakeAgent from "./TemplateReviewerIntakeAgent";
import TemplateReviewerChecklistAgent from "./TemplateReviewerChecklistAgent";
import RedrafterOrchestratorAgent from "./RedrafterOrchestratorAgent";
import TemplateGuidedIntakeAgent from "./TemplateGuidedIntakeAgent";
import SocialMediaIntakeAgent from "./SocialMediaIntakeAgent";
import OrchestratorAgent from "./OrchestratorAgent";
import JDComposerAgent from "./JDComposerAgent";
import JDDetailsAgent from "./JDDetailsAgent";
import InferenceWorkflowEndAgent from "./InferenceWorkflowEndAgent";
import TemplateDocumentSelectorAgent from "./TemplateDocumentSelectorAgent";
import DataTalkIntentClassifierAgent from "./DataTalkIntentClassifierAgent";
import DataTalkQueryPlannerAgent from "./DataTalkQueryPlannerAgent";
import DataTalkSqlGeneratorAgent from "./DataTalkSqlGeneratorAgent";
import SowAutoFillerAgent from "./SowAutoFillerAgent";
import AnswerComposerAgent from "./AnswerComposerAgent";
import LegalReviewerAgent from "./LegalReviewerAgent";
import RfqOrchestratorAgent from "./RfqOrchestratorAgent";
import RfqBuilderAgent from "./RfqBuilderAgent";
import RfqResponseEvaluatorAgent from "./RfqResponseEvaluatorAgent";
import FrameworkApprovalAgent from "./FrameworkApprovalAgent";
import EvaluationResultsAgent from "./EvaluationResultsAgent";
import QAAgent from "./QAAgent";
import NdaOrchestratorAgent from "./NdaOrchestratorAgent";
import JobIntakeAgent from "./JobIntakeAgent";
import DocumentAnalyzerAgent from "./DocumentAnalyzerAgent";
import OutcomeModifierAgent from "./OutcomeModifierAgent";
import OutcomeFinalizerAgent from "./OutcomeFinalizerAgent";
import RedlineAgent from "./RedlineAgent";
import MouOrchestratorAgent from "./MouOrchestratorAgent";
import UtilityAgentWithButtons from "./UtilityAgentWithButtons";
import HumanCapitalAgent from "./HumanCapitalAgent";
import LearningPathUtilityAgentForm from "./LearningPathUtilityAgentForm";
import LearningPathAgent from "./LearningPathAgent";
import LearningPathAgentV2 from "./LearningPathAgentV2";
import TrainingSearchErrorForm from "./TrainingSearchErrorForm";
import TrainingSearchAgent from "./TrainingSearchAgent";
import TalentReportAgent from "./TalentReportAgent";
import TrainingSearchChatAgent from "./TrainingSearchChatAgent";

type AgentResponseProps = {
  agentId: string | undefined;
  content: string;
  inputRef?: React.RefObject<MessageInputHandle>;
  currentChat?: any[];
  messageId?: string;
};

export default function AgentResponse({
  agentId,
  content,
  inputRef,
  currentChat = [],
  messageId,
}: AgentResponseProps): JSX.Element {
  const parsedContent = safeJSONParse(content);

  // Handler for suggestion click - shared across multiple agents
  const handleSuggestionClick = (suggestion: string) => {
    if (inputRef?.current) {
      inputRef.current.sendPrompt(suggestion);
    }
  };

  console.log("PARSED CONTENT", parsedContent);
  console.log("AGENT ID", agentId);

  // ============================================================================
  // UTILITY & GENERAL PURPOSE AGENTS
  // ============================================================================

  // Utility Agent - Generic utility responses
  // Agent IDs: utility_agent
  if (agentId === "utility_agent") {
    return <UtilityAgent parsedContent={parsedContent} />;
  }

  // ============================================================================
  // COMMUNICATION & CONTENT CREATION AGENTS
  // ============================================================================

  // Press Release Agent - Main press release generation and editing
  // Agent IDs: press_release_agent
  if (agentId === "press_release_agent") {
    return (
      <PressReleaseAgent
        parsedContent={parsedContent}
        handleSuggestionClick={handleSuggestionClick}
      />
    );
  }

  // Press Release QA Agent - Quality assurance for press releases
  // Agent IDs: press_release_qa_agent, PressReleaseQAAgent
  if (
    agentId === "press_release_qa_agent" ||
    agentId === "PressReleaseQAAgent"
  ) {
    return <PressReleaseQAAgent parsedContent={parsedContent} />;
  }

  // Localizer Agent - Content localization and regional adaptation
  // Agent IDs: LocalizerAgent, localizer_agent
  if (agentId === "LocalizerAgent" || agentId === "localizer_agent") {
    return (
      <LocalizerAgent
        parsedContent={parsedContent}
        handleSuggestionClick={handleSuggestionClick}
      />
    );
  }

  // Translation Agent - Multi-language translation services
  // Agent IDs: translation_agent
  if (agentId === "translation_agent") {
    return (
      <TranslationAgent
        parsedContent={parsedContent}
        handleSuggestionClick={handleSuggestionClick}
      />
    );
  }

  // Media Brief Agent - Media brief generation and management
  // Agent IDs: media_brief_agent
  if (agentId === "media_brief_agent") {
    return (
      <MediaBriefAgent
        parsedContent={parsedContent}
        handleSuggestionClick={handleSuggestionClick}
      />
    );
  }

  // Social Media Insights Agent - Social media analytics and insights
  // Agent IDs: social_media_insights_agent
  if (agentId === "social_media_insights_agent") {
    return <SocialMediaInsightsAgent parsedContent={parsedContent} />;
  }

  // Social Media Intake Agent - Social media content intake and processing
  // Agent IDs: socialmedia_intake_agent
  if (agentId === "socialmedia_intake_agent") {
    return <SocialMediaIntakeAgent parsedContent={parsedContent} />;
  }

  // Internal Communication Agent - Internal messaging and communication
  // Agent IDs: internal_communication_agent
  if (agentId === "internal_communication_agent") {
    return (
      <InternalCommunicationAgent
        parsedContent={parsedContent}
        handleSuggestionClick={handleSuggestionClick}
      />
    );
  }

  // ============================================================================
  // WORKFLOW ORCHESTRATION & MANAGEMENT AGENTS
  // ============================================================================

  // Orchestrator Agent - General workflow orchestration
  // Agent IDs: orchestrator_new_agent
  if (agentId == "orchestrator_new_agent") {
    return <OrchestratorAgent parsedContent={parsedContent} />;
  }

  // Workflow End Agent - Generic workflow completion
  // Agent IDs: workflow_end
  if (agentId === "workflow_end") {
    return (
      <WorkflowEndAgent
        parsedContent={parsedContent}
        currentChat={currentChat}
      />
    );
  }

  // Press Release / Media Brief Workflow End Agent - Specific workflow completion
  // Agent IDs: pr_mb_workflow_end
  if (agentId === "pr_mb_workflow_end") {
    return (
      <PrMbWorkflowEndAgent
        parsedContent={parsedContent}
        currentChat={currentChat}
      />
    );
  }

  // Inference Workflow End Agent - AI inference workflow completion
  // Agent IDs: inference_workflow_end
  if (agentId === "inference_workflow_end") {
    return <InferenceWorkflowEndAgent parsedContent={parsedContent} />;
  }

  // ============================================================================
  // HUMAN RESOURCES & TALENT MANAGEMENT AGENTS
  // ============================================================================

  // Synthetic Resume Agent - Resume generation, skill inference, and requirement modification
  // Agent IDs: synthetic_resume_agent, skill_inference_agent, modify_requirements
  if (
    agentId === "synthetic_resume_agent" ||
    agentId === "skill_inference_agent" ||
    agentId === "modify_requirements"
  ) {
    return (
      <SyntheticResumeAgent parsedContent={parsedContent} agentId={agentId} />
    );
  }

  // Job Description Details Agent - JD information and specifications
  // Agent IDs: jd_details_agent
  if (agentId == "jd_details_agent") {
    return <JDDetailsAgent parsedContent={parsedContent} />;
  }

  // Job Description Composer Agent - JD creation and composition
  // Agent IDs: jd_composer_agent
  if (agentId === "jd_composer_agent") {
    return <JDComposerAgent parsedContent={parsedContent} />;
  }

  // Job Intake Agent - Job posting intake and initial processing
  // Agent IDs: job_intake_agent
  if (agentId === "job_intake_agent") {
    return <JobIntakeAgent parsedContent={parsedContent} />;
  }

  // Resume Fetcher Agent - Resume retrieval and extraction
  // Agent IDs: resume_fetcher_agent
  if (agentId === "resume_fetcher_agent") {
    return <ResumeFetcherAgent parsedContent={parsedContent} />;
  }

  // Scoring Agent - Candidate scoring and evaluation (legacy and new versions)
  // Agent IDs: scoring_agent, scoring_agent_new
  if (agentId === "scoring_agent" || agentId === "scoring_agent_new") {
    return <ScoringAgent parsedContent={parsedContent} agentId={agentId} />;
  }

  // Email Drafting Agent - Recruitment email generation
  // Agent IDs: email_drafting
  if (agentId === "email_drafting") {
    return <EmailDraftingAgent parsedContent={parsedContent} />;
  }

  // Validation Check Agent - Candidate validation and verification
  // Agent IDs: validation_check
  if (agentId === "validation_check") {
    return <ValidationCheckAgent parsedContent={parsedContent} />;
  }

  // HR Agent V2 HITL Agent - Human-in-the-loop HR decisions and role validation failures
  // Agent IDs: hr_agent_v2_hitl, role_validation_failed
  if (agentId === "hr_agent_v2_hitl" || agentId === "role_validation_failed") {
    return <HrAgentV2HitlAgent parsedContent={parsedContent} />;
  }

  // No Candidates Found Agent - Handles empty candidate search results
  // Agent IDs: no_candidates_found
  if (agentId === "no_candidates_found") {
    return <NoCandidatesFoundAgent parsedContent={parsedContent} />;
  }

  // ============================================================================
  // DATA ANALYTICS & BUSINESS INTELLIGENCE AGENTS
  // ============================================================================

  // DataTalk Intent Classifier Agent - Classifies user query intent for data operations
  // Agent IDs: intent_classifier_agent
  if (agentId == "intent_classifier_agent") {
    return (
      <DataTalkIntentClassifierAgent
        parsedContent={parsedContent}
        handleSuggestionClick={handleSuggestionClick}
      />
    );
  }

  // DataTalk Query Planner Agent - Plans and structures data queries
  // Agent IDs: query_planner_agent
  if (agentId == "query_planner_agent") {
    return <DataTalkQueryPlannerAgent parsedContent={parsedContent} />;
  }

  // DataTalk SQL Generator Agent - Generates SQL queries from natural language
  // Agent IDs: sql_generator_agent
  if (agentId == "sql_generator_agent") {
    return <DataTalkSqlGeneratorAgent parsedContent={parsedContent} />;
  }

  // ============================================================================
  // LEGAL & CONTRACT MANAGEMENT AGENTS
  // ============================================================================

  // Template Reviewer Intake Agent - Template review intake and QA
  // Agent IDs: template_reviewer_intake_agent, template_reviewer_qa_agent
  if (
    agentId === "template_reviewer_intake_agent" ||
    agentId === "template_reviewer_qa_agent"
  ) {
    return <TemplateReviewerIntakeAgent parsedContent={parsedContent} />;
  }

  // Template Guided Intake Agent - Guided template data collection
  // Agent IDs: template_guided_intake_agent
  if (agentId === "template_guided_intake_agent") {
    return <TemplateGuidedIntakeAgent parsedContent={parsedContent} />;
  }

  // Template Reviewer Agent - Main template review functionality
  // Agent IDs: template_reviewer_agent
  if (agentId === "template_reviewer_agent") {
    return <TemplateReviewerAgent parsedContent={parsedContent} />;
  }

  // Template Reviewer Checklist Agent - Template review checklist validation
  // Agent IDs: template_reviewer_checklist_agent
  if (agentId === "template_reviewer_checklist_agent") {
    return (
      <TemplateReviewerChecklistAgent
        parsedContent={parsedContent}
        messageId={messageId || ""}
      />
    );
  }

  // Template Document Selector Agent - Template selection and matching
  // Agent IDs: template_document_selector_agent
  if (agentId === "template_document_selector_agent") {
    return <TemplateDocumentSelectorAgent parsedContent={parsedContent} />;
  }

  // Contract Reviewer Agent - Contract review orchestration and consolidation
  // Handles MSA review, QA, auto MSA processing, version confirmation, consolidation, and redrafting
  // Agent IDs: contract_reviewer_orchestrator, contract_reviewer_qa_agent,
  //            contract_reviewer_orchestrator_auto_msa, contract_reviewer_orchestrator_msa_version_confirm,
  //            ConsolidatorAgent, consolidator_agent, redrafting_agent
  if (
    agentId === "contract_reviewer_orchestrator" ||
    agentId === "contract_reviewer_qa_agent" ||
    agentId === "contract_reviewer_orchestrator_auto_msa" ||
    agentId === "contract_reviewer_orchestrator_msa_version_confirm" ||
    agentId === "ConsolidatorAgent" ||
    agentId === "consolidator_agent" ||
    agentId === "redrafting_agent"
  ) {
    return <ContractReviewerAgent parsedContent={parsedContent} />;
  }

  // Legal Reviewer Orchestrator Agent - Legal document review coordination
  // Agent IDs: legal_reviewer_orchestrator
  if (agentId === "legal_reviewer_orchestrator") {
    return <LegalReviewerAgent parsedContent={parsedContent} />;
  }

  // Redrafter Orchestrator Agent - Document redrafting workflow
  // Agent IDs: redrafter_orchestrator
  if (agentId === "redrafter_orchestrator") {
    return <RedrafterOrchestratorAgent parsedContent={parsedContent} />;
  }

  // ============================================================================
  // PROCUREMENT & RFQ MANAGEMENT AGENTS
  // ============================================================================

  // SOW Auto Filler Agent - Statement of Work automatic population
  // Agent IDs: sow_auto_filler
  if (agentId === "sow_auto_filler") {
    return (
      <SowAutoFillerAgent parsedContent={parsedContent} messageId={messageId} />
    );
  }

  // Answer Composer Agent - RFQ response composition
  // Agent IDs: answer_composer_agent
  if (agentId === "answer_composer_agent") {
    return <AnswerComposerAgent parsedContent={parsedContent} />;
  }

  // RFQ Orchestrator Agent - RFQ workflow orchestration and criteria parsing
  // Agent IDs: rfq_orchestrator, criteria_parser
  if (agentId === "rfq_orchestrator" || agentId === "criteria_parser") {
    return <RfqOrchestratorAgent parsedContent={parsedContent} />;
  }

  // RFQ Builder Agent - RFQ document creation and construction
  // Agent IDs: rfq_builder
  if (agentId === "rfq_builder") {
    return <RfqBuilderAgent parsedContent={parsedContent} />;
  }

  // RFQ Response Evaluator Agent - Comprehensive RFQ evaluation workflow
  // Handles evaluation, file overview, criteria approval, method selection, vendor assignment,
  // score review, technical scoring, completion, uploads, exports, and summaries
  // Agent IDs: rfq_response_evaluator, rfq_evaluator, files_overview, criteria_approval,
  //            method_selection, vendor_assignment, score_review, technical_score_review,
  //            evaluation_complete, document_upload, export_response, summary_response
  if (
    agentId === "rfq_response_evaluator" ||
    agentId === "rfq_evaluator" ||
    agentId === "files_overview" ||
    agentId === "criteria_approval" ||
    agentId === "method_selection" ||
    agentId === "vendor_assignment" ||
    agentId === "score_review" ||
    agentId === "technical_score_review" ||
    agentId === "evaluation_complete" ||
    agentId === "document_upload" ||
    agentId === "export_response" ||
    agentId === "summary_response"
  ) {
    return (
      <RfqResponseEvaluatorAgent
        parsedContent={parsedContent}
        agentId={agentId}
      />
    );
  }

  // Framework Approval Agent - Framework agreement approval workflow
  // Agent IDs: framework_approval_agent
  if (agentId === "framework_approval_agent") {
    return <FrameworkApprovalAgent parsedContent={parsedContent} />;
  }

  // Evaluation Results Agent - RFQ evaluation results presentation
  // Agent IDs: evaluation_results_agent
  if (agentId === "evaluation_results_agent") {
    return <EvaluationResultsAgent parsedContent={parsedContent} />;
  }

  // ============================================================================
  // QUALITY ASSURANCE AGENTS
  // ============================================================================

  // QA Agent - General quality assurance checks
  // Agent IDs: qa_agent
  if (agentId === "qa_agent") {
    return <QAAgent parsedContent={parsedContent} />;
  }

  // ============================================================================
  // NDA (NON-DISCLOSURE AGREEMENT) WORKFLOW AGENTS
  // ============================================================================

  // NDA Orchestrator Agent - Complete NDA workflow orchestration
  // Handles intake, input review, counterparty analysis, conflict checking, template selection,
  // field capture, drafting, validation, review, parsing, QA, and multiparty forms
  // Agent IDs: nda_orchestrator, nda_intake_agent, nda_input_review_agent, nda_counterparty_agent,
  //            nda_conflict_agent, nda_template_selector_agent, nda_draft_field_capture_agent,
  //            nda_drafting_agent, nda_validation_agent, nda_reviewer_agent, nda_parser_agent,
  //            nda_qa_agent, nda_multiparty_form
  if (
    agentId === "nda_orchestrator" ||
    agentId === "nda_intake_agent" ||
    agentId === "nda_input_review_agent" ||
    agentId === "nda_counterparty_agent" ||
    agentId === "nda_conflict_agent" ||
    agentId === "nda_template_selector_agent" ||
    agentId === "nda_draft_field_capture_agent" ||
    agentId === "nda_drafting_agent" ||
    agentId === "nda_validation_agent" ||
    agentId === "nda_reviewer_agent" ||
    agentId === "nda_parser_agent" ||
    agentId === "nda_qa_agent" ||
    agentId === "nda_multiparty_form"
  ) {
    return (
      <NdaOrchestratorAgent
        parsedContent={parsedContent}
        messageId={messageId}
      />
    );
  }

  // ============================================================================
  // COC REVIEW AGENT (CHANGE OF CONTROL) - Agent ID: 103
  // Complete workflow for analyzing Change of Control clauses in contracts
  // Multi-step process: Form intake → Document analysis → Outcome modification → Finalization
  // ============================================================================

  // CoC Review Agent - Step 1: Transaction Details Form
  // Collects transaction information (percentage, type, parties involved)
  // Agent IDs: utility_agent_form
  if (agentId === "utility_agent_form") {
    return (
      <UtilityAgentForm parsedContent={parsedContent} messageId={messageId} />
    );
  }

  // CoC Review Agent - Step 2: Document Analysis & Initial Review
  // Analyzes uploaded contracts for CoC clauses and triggers
  // Agent IDs: document_analyser_executor
  if (agentId === "document_analyser_executor") {
    return (
      <DocumentAnalyzerAgent
        parsedContent={parsedContent}
        messageId={messageId}
      />
    );
  }

  // CoC Review Agent - Step 3: Outcome Modification & Editing
  // Allows user to modify analysis results and recommendations
  // Agent IDs: outcome_modifier_executor
  if (agentId === "outcome_modifier_executor") {
    return (
      <OutcomeModifierAgent
        parsedContent={parsedContent}
        messageId={messageId}
      />
    );
  }

  // CoC Review Agent - Step 4: Final Outcome & Report Download
  // Presents final analysis report with download capability
  // Agent IDs: outcome_finalizer_executor
  if (agentId === "outcome_finalizer_executor") {
    return <OutcomeFinalizerAgent parsedContent={parsedContent} />;
  }

  // ============================================================================
  // MOU AGENT (MEMORANDUM OF UNDERSTANDING) - Agent ID: 54
  // Automated MoU drafting and collaboration agreement generation
  // ============================================================================

  // MoU Orchestrator Agent - Complete MoU workflow orchestration
  // Agent IDs: memorandum-of-understanding, mou_orchestrator, mou_agent, mou_intake_agent
  if (
    agentId === "memorandum-of-understanding" ||
    agentId === "mou_orchestrator" ||
    agentId === "mou_agent" ||
    agentId === "mou_entity_agent" ||
    agentId === "mou_intake_agent"
  ) {
    return (
      <MouOrchestratorAgent
        parsedContent={parsedContent}
        messageId={messageId}
      />
    );
  }

  // ============================================================================
  // REDLINES AGENT (DOCUMENT COMPARISON & MARKUP) - Agent ID: 52
  // Document redlining, change tracking, and version comparison
  // Includes specialized handlers for SOW (Statement of Work) workflows
  // ============================================================================

  // Redlines Agent - Main redline generation and comparison
  // Agent IDs: redline
  if (agentId === "redline") {
    return <RedlineAgent parsedContent={parsedContent} />;
  }

  // Redlines Agent - Intake with directional action buttons
  // Initial document upload and workflow direction selection
  // Agent IDs: utility_agent_redline_intake
  if (agentId === "utility_agent_redline_intake") {
    return (
      <UtilityAgentWithButtons
        parsedContent={parsedContent}
        agentId={agentId}
        messageId={messageId}
      />
    );
  }

  // Redlines Agent - Document Analyzer with correction capability
  // Analyzes documents with option to correct or modify analysis
  // Agent IDs: utility_agent_redline_doc_analyzer
  if (agentId === "utility_agent_redline_doc_analyzer") {
    return (
      <UtilityAgentWithButtons
        parsedContent={parsedContent}
        agentId={agentId}
        messageId={messageId}
      />
    );
  }

  // Redlines Agent - SOW Type Selection (Parent MSA / Standalone)
  // Determines if SOW is standalone or references a parent MSA
  // Agent IDs: utility_agent_redline_doc_analyzer_sow_buttons
  if (agentId === "utility_agent_redline_doc_analyzer_sow_buttons") {
    return (
      <UtilityAgentWithButtons
        parsedContent={parsedContent}
        agentId={agentId}
        messageId={messageId}
      />
    );
  }

  // Redlines Agent - SOW MSA Document Upload Handler
  // Enables parent MSA file upload for SOW analysis
  // Agent IDs: utility_agent_redline_doc_analyzer_sow_msa
  if (agentId === "utility_agent_redline_doc_analyzer_sow_msa") {
    return (
      <UtilityAgentWithButtons
        parsedContent={parsedContent}
        agentId={agentId}
        messageId={messageId}
      />
    );
  }

  // ============================================================================
  // HUMAN CAPITAL AGENT (SKILLS BENCHMARKING) - Agent ID: 55
  // Role-to-skill mapping and benchmarking with taxonomy-driven intelligence
  // Maps job roles to required skills using industry taxonomies
  // ============================================================================

  // Human Capital Agent - Skill mapping workflow
  // Handles skill mapping, modification, and report download
  // Agent IDs: skill_mapping_agent, skill_mapping_agent_modify, skill_mapping_agent_download
  if (
    agentId === "skill_mapping_agent" ||
    agentId === "skill_mapping_agent_modify" ||
    agentId === "skill_mapping_agent_download"
  ) {
    return (
      <HumanCapitalAgent parsedContent={parsedContent} agentId={agentId} />
    );
  }

  // ============================================================================
  // LEARNING PATH AGENTS - Agent ID: 57
  // Personalized learning path generation and training course recommendations
  // ============================================================================

  // Learning Path Utility Agent Form - Data collection for learning path generation
  // Collects user preferences, skill gaps, and learning objectives
  // Agent IDs: learning_path_utility_agent_form
  if (agentId === "learning_path_utility_agent_form") {
    return (
      <LearningPathUtilityAgentForm
        parsedContent={parsedContent}
        messageId={messageId}
      />
    );
  }

  // Learning Path Agent V1 - Structured learning path display
  // Shows personalized learning paths with phases, courses, and milestones
  // Agent IDs: learning_path_agent
  if (agentId === "learning_path_agent") {
    return <LearningPathAgent parsedContent={parsedContent} />;
  }

  // Learning Path Agent V2 - Card-based training search results
  // Displays training courses in grid layout with skills, duration, and links
  // Agent IDs: training_search_results, learning_path_agent_v2
  if (
    agentId === "training_search_results" ||
    agentId === "learning_path_agent_v2"
  ) {
    return <LearningPathAgentV2 parsedContent={parsedContent} />;
  }

  // ============================================================================
  // TRAINING SEARCH AGENT - Agent ID: 58
  // Personalized learning path generation and training course recommendations
  // ============================================================================

  // Training Search Preferences Form - Additional criteria collection
  // Collects budget, timeframe, mode, language, location, certification, capacity
  // Used when initial search needs refinement or more specific parameters
  // Agent IDs: training_search_preferences_form
  if (agentId === "training_search_preferences_form") {
    return (
      <TrainingSearchErrorForm
        parsedContent={parsedContent}
        messageId={messageId}
      />
    );
  }

  // Training Search Agent - Skill-prioritized course recommendations
  // Shows courses organized by skill priority with detailed metadata
  // Includes skill match scores, recommendations, budget/timeframe status, certifications
  // Agent IDs: training_search_agent
  if (agentId === "training_search_agent") {
    return <TrainingSearchAgent parsedContent={parsedContent} />;
  }

  // Training Search Chat Agent - Simple question display for training search flow
  // Displays conversational prompts during training search interaction
  // Agent IDs: training_search_chat
  if (agentId === "training_search_chat") {
    return <TrainingSearchChatAgent parsedContent={parsedContent} />;
  }

  // ============================================================================
  // TALENT SCOUTER AGENT (TALENT ANALYTICS & REPORTING) - Agent ID: 59
  // Talent pipeline analytics, sourcing breakdown, and candidate quality insights
  // ============================================================================

  // Talent Scouter Agent - Talent report with dashboard metrics
  // Shows candidate pipeline overview, sourcing channels, and quality indicators
  // Agent IDs: talent_report_agent
  if (agentId === "talent_report_agent") {
    return <TalentReportAgent parsedContent={parsedContent} />;
  }

  return (
    <div>
      <div>Something went wrong, please try again!</div>
    </div>
  );
}
